/**
 * AnalysisDetailModal Component
 *
 * Quick-look modal for viewing past analysis results.
 * Features:
 * - Full message text display
 * - Analysis score and category
 * - "Edit & Re-analyze" action (navigates to home with pre-filled message)
 * - "Done" action (closes modal)
 */
import { Modal, View, Text, Pressable, ScrollView, ActivityIndicator } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import type { AnalysisRecord } from "@dramadetector/api-client";
import { useAnalysisResult } from "../hooks/useAnalysisResult";

interface AnalysisDetailModalProps {
  visible: boolean;
  analysis: AnalysisRecord | null;
  onClose: () => void;
  onEditAndReanalyze: (message: string) => void;
}

// Translate a raw API category key using the history_item.category namespace
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translateCategory = (t: any, key: string): string => {
  const translated = t(`history_item.category.${key}`);
  if (!translated || translated.startsWith('history_item.category.')) {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return translated;
};

export function AnalysisDetailModal({
  visible,
  analysis,
  onClose,
  onEditAndReanalyze,
}: AnalysisDetailModalProps) {
  const { t } = useTranslation('analysis', { useSuspense: false });
  const { t: tHistory } = useTranslation('history', { useSuspense: false });
  const { data: decryptedData, isLoading } = useAnalysisResult(analysis?.id);

  if (!analysis) return null;

  const { score, category, createdAt } = analysis;
  const message = decryptedData?.request.message ?? decryptedData?.message ?? analysis.message;

  // Determine color scheme based on category
  // 4-category system: safe, caution, high_drama, extreme_drama
  const colorSchemes: Record<string, { bg: string; text: string; border: string }> = {
    safe: {
      bg: "bg-safe/20",
      text: "text-safe",
      border: "border-safe/40",
    },
    caution: {
      bg: "bg-caution/20",
      text: "text-caution",
      border: "border-caution/40",
    },
    high_drama: {
      bg: "bg-orange-500/20",
      text: "text-orange-500",
      border: "border-orange-500/40",
    },
    extreme_drama: {
      bg: "bg-danger/20",
      text: "text-danger",
      border: "border-danger/40",
    },
  };
  const colors = colorSchemes[category] || {
    bg: "bg-gray-100",
    text: "text-gray-700",
    border: "border-gray-300",
  };

  // Format timestamp
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View className="flex-1 bg-gray-50">
        {/* Header */}
        <View className="bg-white border-b border-gray-200 px-4 pt-12 pb-4">
          <View className="flex-row justify-between items-center">
            <Text className="text-xl font-bold text-gray-900">
              {t('analysis_detail_modal.title')}
            </Text>
            <Pressable
              onPress={onClose}
              className="w-8 h-8 items-center justify-center"
              style={{ cursor: 'pointer' }}
              accessibilityRole="button"
              accessibilityLabel={t('analysis_detail_modal.accessibility.close_modal')}
            >
              <Text className="text-2xl text-gray-600">×</Text>
            </Pressable>
          </View>
        </View>

        {/* Content */}
        <ScrollView className="flex-1">
          <View className="p-4 max-w-2xl mx-auto w-full">
            {/* Score Badge */}
            <View className={`${colors.bg} ${colors.border} border px-4 py-3 rounded-xl mb-4 flex-row items-center justify-between`}>
              <View>
                <Text className={`text-2xl font-bold ${colors.text}`}>
                  {score.toFixed(1)}
                </Text>
                <Text className={`text-xs font-medium ${colors.text} uppercase mt-0.5`}>
                  {translateCategory(tHistory, category)}
                </Text>
              </View>
              <Text className="text-xs text-gray-600">
                {formatDate(createdAt)}
              </Text>
            </View>

            {/* Message */}
            <View className="bg-white rounded-xl p-4 border border-gray-200 mb-4">
              <Text className="text-sm font-semibold text-gray-700 mb-2">
                {t('analysis_detail_modal.message_label')}
              </Text>
              {isLoading ? (
                <ActivityIndicator size="small" color="#6b7280" />
              ) : (
                <Text className="text-base text-gray-900 leading-relaxed">
                  {message}
                </Text>
              )}
            </View>

            {/* Action Buttons */}
            <View className="gap-3 mb-6">
              {/* Primary CTA: Edit & Re-analyze */}
              <Pressable
                onPress={() => {
                  if (message) {
                    onEditAndReanalyze(message);
                    onClose();
                  }
                }}
                disabled={isLoading || !message}
                className="bg-blue-600 rounded-xl py-4 px-6 active:bg-blue-700 disabled:opacity-50"
                style={{ cursor: 'pointer' }}
                accessibilityRole="button"
                accessibilityLabel={t('analysis_detail_modal.accessibility.edit_and_reanalyze')}
              >
                <Text className="text-white text-center font-semibold text-base">
                  {t('analysis_detail_modal.actions.edit_and_reanalyze')}
                </Text>
              </Pressable>

              {/* Secondary CTA: Done */}
              <Pressable
                onPress={onClose}
                className="bg-gray-100 rounded-xl py-4 px-6 active:bg-gray-200"
                style={{ cursor: 'pointer' }}
                accessibilityRole="button"
                accessibilityLabel={t('analysis_detail_modal.accessibility.close_modal')}
              >
                <Text className="text-gray-700 text-center font-semibold text-base">
                  {t('analysis_detail_modal.actions.done')}
                </Text>
              </Pressable>
            </View>
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
}
